/* Les documents d'une usine, sur sa fiche.
 *
 * On dépose en glissant le fichier dessus ; la nature se devine au nom et se
 * corrige d'un clic. Rien ne s'ouvre sans un lien signé d'une heure : le bac
 * n'est pas public, et un catalogue qui fuite nomme l'usine en couverture.
 */

import { useState } from "react";
import { NATURES, nomNature, poidsCourt, refusDe } from "@/domain/documents-usine";
import { autorise, useNiveau } from "@/lib/session";
import { Empty, Section, Tag } from "@/ui/primitives";
import { Icon } from "@/ui/Icon";
import { useToast } from "@/ui/Toast";
import { dateFr } from "@/lib/format";
import { DepotRefuse, deposerDocument, lienDocument, renommerDocument, retirerDocument }
  from "./actions";
import type { DocumentUsine, NatureDocument, Produit } from "@/types/metier";

export function DocumentsUsine({
  usineId, documents, produits, chargement, onChange,
}: {
  usineId: string;
  documents: DocumentUsine[];
  produits: Produit[];
  chargement: boolean;
  onChange: () => void;
}) {
  const toast = useToast();
  const niveau = useNiveau();
  const peutEcrire = autorise(niveau, "agent");
  const [survol, setSurvol] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const [ouvert, setOuvert] = useState<string | null>(null);

  async function deposer(fichiers: FileList | null) {
    if (!fichiers?.length) return;
    setOccupe(true);
    let poses = 0;
    for (const f of Array.from(fichiers)) {
      const refus = refusDe(f);
      if (refus) {
        toast(`${f.name} — ${refus}`);
        continue;
      }
      try {
        await deposerDocument(usineId, f);
        poses += 1;
      } catch (e) {
        toast(
          `${f.name} — ${e instanceof DepotRefuse || e instanceof Error ? e.message : "dépôt impossible"}`,
        );
      }
    }
    setOccupe(false);
    if (poses) {
      toast(`${poses} document${poses > 1 ? "s" : ""} déposé${poses > 1 ? "s" : ""}.`);
      onChange();
    }
  }

  async function ouvrir(d: DocumentUsine) {
    const url = await lienDocument(d.chemin);
    if (!url) {
      toast("Ce fichier est introuvable dans le bac.");
      return;
    }
    window.open(url, "_blank", "noopener");
  }

  async function changer(d: DocumentUsine, champs: Parameters<typeof renommerDocument>[1]) {
    try {
      await renommerDocument(d.id, champs);
      onChange();
    } catch (e) {
      toast(e instanceof Error ? e.message : "La modification a échoué.");
    }
  }

  async function retirer(d: DocumentUsine) {
    setOccupe(true);
    try {
      await retirerDocument(d);
      toast(`${d.titre} retiré.`);
      onChange();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Le retrait a échoué.");
    } finally {
      setOccupe(false);
    }
  }

  /* Groupés par nature, dans l'ordre où on les cherche : la présentation et le
     catalogue d'abord, les fiches ensuite. */
  const groupes = NATURES.map((n) => ({
    ...n,
    docs: documents.filter((d) => d.nature === n.cle),
  })).filter((g) => g.docs.length);

  return (
    <Section
      titre="Documents"
      note={chargement ? "chargement…" : `${documents.length} fichiers`}
    >
      {peutEcrire ? (
        <label
          className={`depot depot-court${survol ? " on" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setSurvol(true);
          }}
          onDragLeave={() => setSurvol(false)}
          onDrop={(e) => {
            e.preventDefault();
            setSurvol(false);
            void deposer(e.dataTransfer.files);
          }}
        >
          <Icon n="importer" cls="depot-i" />
          <div className="depot-t">
            {occupe ? "Dépôt en cours…" : "Déposez présentations, catalogues et fiches"}
          </div>
          <div className="depot-n">
            PDF, Word, Excel, PowerPoint, image ou zip — 25 Mo par fichier
          </div>
          <input
            type="file"
            multiple
            className="depot-champ"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.zip"
            disabled={occupe}
            onChange={(e) => {
              void deposer(e.target.files);
              e.target.value = "";
            }}
          />
        </label>
      ) : null}

      {!documents.length ? (
        <Empty
          titre="Aucun document"
          detail={
            peutEcrire
              ? "La plaquette, le catalogue et les fiches techniques se rangent ici."
              : "Cette usine n'a pas encore de document diffusable."
          }
        />
      ) : (
        groupes.map((g) => (
          <div className="doc-groupe" key={g.cle}>
            <div className="doc-groupe-t">
              {g.nom}
              <span className="doc-groupe-n">{g.docs.length}</span>
            </div>
            <div className="rows">
              {g.docs.map((d) => (
                <div className="row" key={d.id}>
                  <div className="row-rail" />
                  <div className="row-body">
                    <div className="row-t">
                      <button type="button" className="lien" onClick={() => void ouvrir(d)}>
                        {d.titre}
                      </button>
                      {d.diffusable ? <Tag ton="accent">diffusable</Tag> : null}
                      {d.produit_id ? (
                        <Tag>{produits.find((p) => p.id === d.produit_id)?.ref ?? "référence"}</Tag>
                      ) : null}
                    </div>
                    <div className="row-meta">
                      {d.nom} · {poidsCourt(Number(d.taille))} · {dateFr(d.cree_le)}
                    </div>
                  </div>
                  {peutEcrire ? (
                    <div className="row-d">
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label={`Modifier ${d.titre}`}
                        title="Modifier"
                        onClick={() => setOuvert(ouvert === d.id ? null : d.id)}
                      >
                        <Icon n="modeles" cls="" />
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label={`Retirer ${d.titre}`}
                        title="Retirer"
                        disabled={occupe}
                        onClick={() => void retirer(d)}
                      >
                        <Icon n="fermer" cls="" />
                      </button>
                    </div>
                  ) : null}

                  {ouvert === d.id && peutEcrire ? (
                    <div className="doc-edit">
                      <div className="champ">
                        <label htmlFor={`t-${d.id}`}>Titre</label>
                        <input
                          id={`t-${d.id}`}
                          defaultValue={d.titre}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v && v !== d.titre) void changer(d, { titre: v });
                          }}
                        />
                      </div>
                      <div className="champ">
                        <label htmlFor={`n-${d.id}`}>Nature</label>
                        <select
                          id={`n-${d.id}`}
                          value={d.nature}
                          onChange={(e) =>
                            void changer(d, { nature: e.target.value as NatureDocument })
                          }
                        >
                          {NATURES.map((n) => (
                            <option key={n.cle} value={n.cle}>{n.nom}</option>
                          ))}
                        </select>
                      </div>
                      <div className="champ">
                        <label htmlFor={`p-${d.id}`}>Référence visée</label>
                        <select
                          id={`p-${d.id}`}
                          value={d.produit_id ?? ""}
                          onChange={(e) =>
                            void changer(d, { produit_id: e.target.value || null })
                          }
                        >
                          <option value="">toute l'usine</option>
                          {produits.map((p) => (
                            <option key={p.id} value={p.id}>{p.ref} — {p.nom}</option>
                          ))}
                        </select>
                      </div>
                      <label className="doc-case" htmlFor={`d-${d.id}`}>
                        <input
                          id={`d-${d.id}`}
                          type="checkbox"
                          checked={d.diffusable}
                          onChange={(e) => void changer(d, { diffusable: e.target.checked })}
                        />
                        Visible des agents — décoché, le document reste à la direction
                      </label>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {documents.length && !groupes.length ? (
        <Empty titre="Rien à montrer" detail={`Nature inconnue : ${nomNature("autre")}.`} />
      ) : null}
    </Section>
  );
}
